import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "./Navbar";
import Footer from "./Footer";
import SEO from "./SEO";
import { useAppData } from "../context/AppDataContext";
import { tourHasPreContract } from "../constants/tourPreContract";
import { linkifyContactText } from "../utils/linkifyContactText";

const TOUR_TITLES = {
  alicante: {
    hr: "Alicante – Escapada de Primavera",
    en: "Alicante – Spring Escape",
    de: "Alicante – Frühlingsausflug",
    es: "Alicante – Escapada de Primavera",
  },
  "alicante-maj": {
    hr: "Alicante – Escapada de Primavera (svibanj)",
    en: "Alicante – Spring Escape (May)",
    de: "Alicante – Frühlingsausflug (Mai)",
    es: "Alicante – Escapada de Primavera (mayo)",
  },
  "alicante-ozujak": {
    hr: "Alicante – Escapada de Primavera (ožujak)",
    en: "Alicante – Spring Escape (March)",
    de: "Alicante – Frühlingsausflug (März)",
    es: "Alicante – Escapada de Primavera (marzo)",
  },
  "madrid-segovia": {
    hr: "Madrid & Segovia – City Break",
    en: "Madrid & Segovia – City Break",
    de: "Madrid & Segovia – City Break",
    es: "Madrid & Segovia – City Break",
  },
  "od-madrida-do-vinograda": {
    hr: "Od Madrida do vinograda",
    en: "From Madrid to Vineyards",
    de: "Von Madrid zu den Weinbergen",
    es: "De Madrid a los viñedos",
  },
  "valladolid-1tjedan": {
    hr: "Valladolid učenje španjolskog jezika",
    en: "Valladolid – Spanish language course",
    de: "Valladolid – Spanischkurs",
    es: "Valladolid – curso de español",
  },
  "valladolid-2tjedan": {
    hr: "Valladolid učenje španjolskog jezika",
    en: "Valladolid – Spanish language course",
    de: "Valladolid – Spanischkurs",
    es: "Valladolid – curso de español",
  },
  "valladolid-1tjedan-lipanj": {
    hr: "Valladolid učenje španjolskog jezika (13.–21. 6. 2026.)",
    en: "Valladolid – Spanish course (13–21 June 2026)",
    de: "Valladolid – Spanischkurs (13.–21. Juni 2026)",
    es: "Valladolid – curso de español (13–21 jun 2026)",
  },
  "valladolid-2tjedan-lipanj": {
    hr: "Valladolid učenje španjolskog jezika (13.–27. 6. 2026.)",
    en: "Valladolid – Spanish course (13–27 June 2026)",
    de: "Valladolid – Spanischkurs (13.–27. Juni 2026)",
    es: "Valladolid – curso de español (13–27 jun 2026)",
  },
  valencia: {
    hr: "Valencia – Las Fallas (privatni aranžman)",
    en: "Valencia – Las Fallas (private arrangement)",
    de: "Valencia – Las Fallas (privates Arrangement)",
    es: "Valencia – Las Fallas (arreglo privado)",
  },
};

const TOUR_DESCRIPTIONS = {
  alicante: {
    hr: "Proljetni bijeg na Costa Blancu – vino, more i radionica paelle.",
    en: "Spring escape to the Costa Blanca – wine, sea and paella workshop.",
    de: "Frühlingsflucht an die Costa Blanca – Wein, Meer und Paella-Workshop.",
    es: "Escape primaveral a la Costa Blanca – vino, mar y taller de paella.",
  },
  "alicante-maj": {
    hr: "Proljetni bijeg 07.–10.05. – Ryanair, 590 €, min. 8 osoba.",
    en: "Spring escape 7–10 May – Ryanair, €590, min. 8 people.",
    de: "Frühlingsausflug 7.–10. Mai – Ryanair, 590 €, min. 8 Personen.",
    es: "Escapada 7–10 mayo – Ryanair, 590 €, mín. 8 personas.",
  },
  "alicante-ozujak": {
    hr: "Proljetni bijeg 07.–10.03. – Ryanair, 590 €, min. 8 osoba.",
    en: "Spring escape 7–10 March – Ryanair, €590, min. 8 people.",
    de: "Frühlingsausflug 7.–10. März – Ryanair, 590 €, min. 8 Personen.",
    es: "Escapada 7–10 marzo – Ryanair, 590 €, mín. 8 personas.",
  },
  "madrid-segovia": {
    hr: "Tapas, Rooftops & španjolska energija. Iberia, 760 €, 4 dana / 3 noćenja.",
    en: "Tapas, rooftops & Spanish energy. Iberia, €760, 4 days / 3 nights.",
    de: "Tapas, Rooftops & spanische Energie. Iberia, 760 €, 4 Tage / 3 Nächte.",
    es: "Tapas, rooftops y energía española. Iberia, 760 €, 4 días / 3 noches.",
  },
  "od-madrida-do-vinograda": {
    hr: "Fiesta San Lorenzo, Ribera del Duero i Segovia: 10.-15.09. ili 15.-20.10.2026.",
    en: "Fiesta San Lorenzo, Ribera del Duero and Segovia: 10-15 Sep or 15-20 Oct 2026.",
    de: "Fiesta San Lorenzo, Ribera del Duero und Segovia: 10.-15.09. oder 15.-20.10.2026.",
    es: "Fiesta San Lorenzo, Ribera del Duero y Segovia: 10-15 sep o 15-20 oct 2026.",
  },
  "valladolid-1tjedan": {
    hr: "Tečaj u Valladolidu, Madrid, Segovia i Salamanca – 1.790 €, kolovoz 2026.",
    en: "Course in Valladolid, Madrid, Segovia & Salamanca – €1,790, August 2026.",
    de: "Kurs in Valladolid, Madrid, Segovia & Salamanca – 1.790 €, August 2026.",
    es: "Curso en Valladolid, Madrid, Segovia y Salamanca – 1.790 €, agosto 2026.",
  },
  "valladolid-2tjedan": {
    hr: "Dva tjedna tečaja u Valladolidu, Madrid, Segovia i Salamanca – 2.490 €, kolovoz–rujan 2026.",
    en: "Two weeks in Valladolid, Madrid, Segovia & Salamanca – €2,490, Aug–Sep 2026.",
    de: "Zwei Wochen Kurs in Valladolid, Madrid, Segovia & Salamanca – 2.490 €, Aug./Sep. 2026.",
    es: "Dos semanas en Valladolid, Madrid, Segovia y Salamanca – 2.490 €, ago–sep 2026.",
  },
  "valladolid-1tjedan-lipanj": {
    hr: "Valladolid učenje španjolskog jezika: 13.–21. 6. 2026., 9 dana / 8 noći, 1.790 €.",
    en: "Valladolid Spanish course: 13–21 June 2026, 9 days / 8 nights, €1,790.",
    de: "Spanischkurs Valladolid: 13.–21. Juni 2026, 9 Tage / 8 Nächte, 1.790 €.",
    es: "Curso de español en Valladolid: 13–21 jun 2026, 9 días / 8 noches, 1.790 €.",
  },
  "valladolid-2tjedan-lipanj": {
    hr: "Valladolid učenje španjolskog jezika: 13.–27. 6. 2026., 14 dana / 13 noći, 2.490 €.",
    en: "Valladolid Spanish course: 13–27 June 2026, 14 days / 13 nights, €2,490.",
    de: "Spanischkurs Valladolid: 13.–27. Juni 2026, 14 Tage / 13 Nächte, 2.490 €.",
    es: "Curso de español en Valladolid: 13–27 jun 2026, 14 días / 13 noches, 2.490 €.",
  },
  valencia: {
    hr: "Las Fallas, privatni luksuzni apartman u centru, asistencija vodiča. Popunjeno – privatno.",
    en: "Las Fallas, luxury private apartment in the centre, guide assistance. Full – private.",
    de: "Las Fallas, luxuriöses Privatapartment im Zentrum, Betreuung. Ausgebucht – privat.",
    es: "Las Fallas, apartamento privado de lujo en el centro, asistencia de guía. Completo – privado.",
  },
};

function formatDate(dateStr, lng) {
  const date = new Date(dateStr);
  return date.toLocaleDateString(lng || "hr-HR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function isGalleryVideoUrl(url) {
  if (!url || typeof url !== "string") return false;
  return /\.(mp4|webm|mov|ogg)(\?.*)?$/i.test(url);
}

export default function TourDetail() {
  const { tourKey } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lng = i18n.language || "hr";
  const base = lng.slice(0, 2);
  const { tours: currentTours } = useAppData();
  const tour = currentTours.find((t) => t.key === tourKey);

  if (!tour) {
    return (
      <div className="app">
        <Navbar />
        <main>
          <section className="section" style={{ paddingTop: "6rem" }}>
            <div className="container">
              <h1>Putovanje nije pronađeno</h1>
              <button onClick={() => navigate("/blog")} className="btn primary">
                Povratak na blog
              </button>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  const titleMap = TOUR_TITLES[tour.key];
  const title =
    titleMap?.[base] ||
    titleMap?.en ||
    (typeof tour.key === "string" ? tour.key.replace(/-/g, " ") : "") ||
    "Putovanje";
  const descMap = TOUR_DESCRIPTIONS[tour.key];
  const blogSnippet = tour.blogText?.[base] || tour.blogText?.en || "";
  const desc =
    descMap?.[base] ||
    descMap?.en ||
    blogSnippet.slice(0, 280).trim() + (blogSnippet.length > 280 ? "…" : "") ||
    t("tourDetail.fallbackDescription", "Pogledajte detalje i raspored putovanja.");

  return (
    <div className="app">
      <SEO 
        title={`${title} - TraveLina`}
        description={desc}
        keywords={`${title}, putovanje, TraveLina, Španjolska`}
        image={tour.image ? (tour.image.startsWith("http") ? tour.image : `https://www.linatravelina.eu${tour.image}`) : undefined}
      />
      <Navbar />
      <main>
        <section className="section" style={{ paddingTop: "6rem" }}>
          <div className="container">
            <button
              type="button"
              className="btn-page-back"
              onClick={() => navigate("/blog")}
            >
              ← {t("blog.btnBack") || "Vrati nazad"}
            </button>

            <article className="tour-detail">
              <div
                className="tour-detail-image"
                style={{ backgroundImage: `url(${tour.image})` }}
              >
                <div className="tour-tag">{tour.tag}</div>
              </div>

              <div className="tour-detail-body">
                <h1>{title}</h1>
                <div className="tour-meta">
                  <span>📍 {tour.country}</span>
                  <span className="tour-meta-date">
                    🗓 {formatDate(tour.date, lng)}
                  </span>
                  <span>
                    ⏳ {tour.days} {t("blog.days")} / {tour.nights}{" "}
                    {t("tours.nights")}
                  </span>
                </div>
                <p className="tour-description">{desc}</p>

                {/* Galerija fotografija */}
                {tour.gallery && tour.gallery.length > 0 && (
                  <div className="blog-gallery" style={{ marginTop: "2rem" }}>
                    <h2 style={{ fontSize: "1.3rem", marginBottom: "1rem", color: "#f97316" }}>
                      Galerija
                    </h2>
                    <div className="gallery-grid">
                      {tour.gallery.map((item, idx) =>
                        isGalleryVideoUrl(item) ? (
                          <div key={idx} className="gallery-item gallery-item-video">
                            <video
                              className="tour-gallery-video"
                              src={item}
                              controls
                              playsInline
                              preload="metadata"
                              title={`${title} ${idx + 1}`}
                            />
                          </div>
                        ) : (
                          <div
                            key={idx}
                            className="gallery-item"
                            onClick={() => {
                              window.open(item, "_blank");
                            }}
                          >
                            <img
                              src={item}
                              alt={`${title} ${idx + 1}`}
                              style={{
                                width: "100%",
                                height: "200px",
                                objectFit: "cover",
                                borderRadius: "0.5rem",
                                cursor: "pointer",
                                transition: "transform 0.2s",
                              }}
                              onError={(e) => {
                                e.target.style.display = "none";
                                e.target.parentElement.style.display = "none";
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.transform = "scale(1.05)";
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.transform = "scale(1)";
                              }}
                            />
                          </div>
                        )
                      )}
                    </div>
                  </div>
                )}

                {/* Detaljne informacije o putovanju */}
                {tour.detailedInfo && tour.detailedInfo[base] && (
                  <div className="blog-detailed-info">
                    {linkifyContactText(tour.detailedInfo[base])}
                  </div>
                )}

                {/* Blog tekst */}
                {tour.blogText && (tour.blogText[base] || tour.blogText.en) && (
                  <div className="blog-full-text">
                    {linkifyContactText(
                      tour.blogText[base] || tour.blogText.en
                    )}
                  </div>
                )}

                <div className="tour-detail-footer" style={{ marginTop: "2rem" }}>
                  <div className="tour-price">
                    <span className="tour-price-main">
                      {tour.price > 0
                        ? `${t("tours.from")} ${tour.price} €`
                        : t("tourDetail.priceOnRequest", "Na upit")}
                    </span>
                    {tour.price > 0 ? (
                      <span className="tour-price-note">{t("tours.perPerson")}</span>
                    ) : null}
                  </div>
                  <div style={{ display: "flex", gap: "1rem", flexWrap: "wrap" }}>
                    {tourHasPreContract(tour.key) && (
                      <button
                        type="button"
                        className="btn ghost btn-precontract"
                        onClick={() => navigate(`/tour/${tour.key}/precontract`)}
                        style={{ marginRight: "0.5rem" }}
                      >
                        {t("tourDetail.preContractInfo") || "Predugovorne informacije"}
                      </button>
                    )}
                    <button
                      className="btn primary"
                      onClick={() => {
                        navigate("/");
                        setTimeout(() => {
                          const contactSection = document.getElementById("contact");
                          if (contactSection) {
                            contactSection.scrollIntoView({ behavior: "smooth" });
                          }
                        }, 100);
                      }}
                    >
                      {t("blog.btnContact") || "Kontakt"}
                    </button>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

